import React, { ChangeEvent, FC } from 'react'
import { Facet } from 'transfix-ui/components/Facet'
import {
  IFilterAutocompleteProp,
  isAutosuggestResult,
} from 'transfix-ui/components/FilterAutocomplete'
import Box from 'transfix-ui/core/Box'
import { RequestForProposalFilters as IRFPFilters } from 'apps/marketplace/apiTypes/requestForProposals'
import AdminFilterAutocomplete from 'apps/marketplace/components/AdminFilterAutocomplete'
import ShipperFilterAutocomplete from 'apps/marketplace/components/ShipperFilterAutocomplete'
import { TextInput } from 'components/atoms'
import { UserIcon } from 'transfix-icons'
import { RFPTypeFilter } from './RFPTypeFilter'
import { StatusFilter } from './StatusFilter'

interface IRequestForProposalsFiltersProps {
  filters?: IRFPFilters
  setFilters: (filters: Partial<IRFPFilters>) => void
}

export const RequestForProposalsFilters: FC<IRequestForProposalsFiltersProps> = ({
  filters = {},
  setFilters,
}: IRequestForProposalsFiltersProps) => {
  const { id, rfpTypes, salesforceRfpId, salesforceRfpTitle, statuses } = filters
  const onTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setFilters({ salesforceRfpTitle: value || undefined })
  const onIdChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setFilters({ id: Number(value) || undefined })
  const onSalesforceIdChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setFilters({ salesforceRfpId: value || undefined })
  const onShipperChange = (val: IFilterAutocompleteProp) => {
    if (isAutosuggestResult(val)) {
      setFilters({
        shipperApplicationId: Number(val.id),
      })
    } else if (val === null) {
      setFilters({
        shipperApplicationId: undefined,
      })
    }
  }
  const onStatusesChange = (selectedStatuses: string[]) =>
    setFilters({
      statuses: selectedStatuses,
    })
  const onRFPTypeChange = (selectedTypes: string[]) =>
    setFilters({
      rfpTypes: selectedTypes,
    })
  const onAdminChange = (val: IFilterAutocompleteProp) => {
    if (isAutosuggestResult(val)) {
      setFilters({
        userId: Number(val.id),
      })
    } else if (val === null) {
      setFilters({
        userId: undefined,
      })
    }
  }
  const onCurrentUserSelect = (isSelected: boolean) =>
    setFilters({
      currentUser: isSelected || undefined,
    })

  return (
    <Box display='flex' alignItems='flex-end' flexWrap='wrap'>
      <Box width='auto' display='flex' alignItems='flex-end' mt={1} mr={2}>
        <TextInput
          label='ID'
          value={id}
          onChange={onIdChange}
          placeholder='RFP ID'
          className='dib w-auto'
          id='request-for-proposals-filter-rfpId'
          dataTest='rfp-filter-rfpId'
        />
      </Box>
      <Box width='auto' display='flex' alignItems='flex-end' mt={1} mr={2}>
        <TextInput
          label='Salesforce RFP Title'
          value={salesforceRfpTitle}
          onChange={onTitleChange}
          placeholder='Salesforce RFP Title'
          className='dib w-auto'
          id='request-for-proposals-filter-salesforceRfpTitle'
          dataTest='rfp-filter-salesforceRfpTitle'
        />
      </Box>
      <Box width='auto' display='flex' alignItems='flex-end' mt={1} mr={2}>
        <TextInput
          label='Salesforce RFP ID'
          value={salesforceRfpId}
          onChange={onSalesforceIdChange}
          placeholder='Salesforce RFP ID'
          className='dib w-auto'
          id='request-for-proposals-filter-salesforceRfpId'
          dataTest='rfp-filter-salesforceRfpId'
        />
      </Box>
      <Box width='auto' mt={1} mr={2}>
        <ShipperFilterAutocomplete label='Shipper' onSelectedValue={onShipperChange} />
      </Box>
      <Box width='auto' mt={1} mr={2}>
        <StatusFilter statuses={statuses} onSelect={onStatusesChange} />
      </Box>
      <Box width='auto' mt={1} mr={2}>
        <RFPTypeFilter options={rfpTypes} onSelect={onRFPTypeChange} />
      </Box>
      <Box width='auto' mt={1} mr={2}>
        <Facet
          id='currentUser'
          dataTest='current-user-filter'
          label='Created by Me'
          icon={<UserIcon size='large' />}
          onClick={onCurrentUserSelect}
          selected={!!filters?.currentUser}
          isLoading={false}
        />
      </Box>
      <Box width='auto' mt={1} mr={2}>
        <AdminFilterAutocomplete label='Creator' onSelectedValue={onAdminChange} />
      </Box>
    </Box>
  )
}
